import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@material-ui/core';
import { Subject as SubjectIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import CardContent from '../../../common/card/CardContent';
import InlineForm from '../../../common/inline/InlineForm';
import InlineHtml from '../../../common/inline/InlineHtml';
import InlineFormik from '../../../common/inline/InlineFormik';
import { InlineFileValue } from '../../../common/inline/InlineFile';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useLinkTransform } from '../../../common/HeaderBackButton';
import { useUpdateTask } from '../../tasks/Task.hooks';
import ProductRiskSelector from '../components/ProductRiskSelector';
import { useUpdateProductUserNeed } from '../Product.hooks';
import {
  ProductUserNeed,
  BaseProductUserNeedsCardProps
} from '../Product.interface';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { getProductEntityName } from '../Product.config';
import environment from '../../../environments';

const UserNeedInfoCard: React.FC<BaseProductUserNeedsCardProps> = (
  props: BaseProductUserNeedsCardProps
) => {
  const {
    title = 'Details',
    productId,
    productUserNeed,
    productUserNeedVersion,
    hidden = false,
    enableTitleRedirect = false,
    onChange,
    onUpdate,
    onError
  } = props;
  const initialData = productUserNeedVersion || productUserNeed;
  const initialValues: Partial<ProductUserNeed> = {
    title: initialData.title || '',
    description: initialData.description || '',
    risk: initialData.risk
  };
  const getPath = useLinkTransform(
    `/portal/products/${productId}/user-needs/${productUserNeed.id}`,
    [{ query: 'from' }]
  );
  const {
    updatedProductUserNeed,
    isUpdatingProductUserNeed: isUpdating,
    updateProductUserNeedError: updateError,
    updateProductUserNeed
  } = useUpdateProductUserNeed(productId, productUserNeed.id);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(productUserNeed.task.id || 0);
  const error: FetchError | undefined = updateError || updateTaskError;
  const { isFieldDisabled } = useTaskEntityFields(productUserNeed.task);

  // notify parent when a user need info is updated
  useEffect(() => {
    if (updatedProductUserNeed) {
      onChange?.(updatedProductUserNeed);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProductUserNeed]);

  useEffect(() => {
    if (updatedTask) {
      productUserNeed.task = Object.assign(
        productUserNeed.task || {},
        updatedTask
      );
      onChange?.(productUserNeed);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <InlineFormik
      initialValues={initialValues}
      isSubmitting={isUpdating}
      error={updateError}
      onSubmit={(values: Partial<ProductUserNeed>) => {
        updateProductUserNeed(values);
        onChange?.(values);
      }}
    >
      <Card style={hidden ? { display: 'none' } : undefined}>
        <CardTitle>
          <SubjectIcon />
          {enableTitleRedirect ? (
            <Link className='link' to={getPath()}>
              {title}
            </Link>
          ) : (
            title
          )}
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            <InlineTextField
              name='title'
              label='Title'
              placeholder={`Enter title for Product ${getProductEntityName(
                'userNeed'
              )}`}
              disabled={
                Boolean(productUserNeedVersion) ||
                isUpdating ||
                isFieldDisabled('title')
              }
            />
            {!environment.productConfig?.userNeed?.hideRiskField && (
              <ProductRiskSelector
                name='risk'
                label='Risk'
                placeholder='Set the Risk value'
                disabled={
                  Boolean(productUserNeedVersion) ||
                  isUpdating ||
                  isFieldDisabled('risk')
                }
              />
            )}
            <InlineHtml
              name='description'
              label='Description'
              helperText={`Provide an overview of the ${getProductEntityName(
                'userNeed'
              )}.`}
              disabled={
                Boolean(productUserNeedVersion) ||
                isUpdating ||
                isFieldDisabled('description')
              }
              attachments={
                productUserNeedVersion?.attachments ||
                productUserNeed.task.attachments ||
                []
              }
              onChangeAttachments={(attachments: InlineFileValue[]) => {
                updateTask({ attachments });
                if (productUserNeed.task) {
                  productUserNeed.task.attachments = attachments;
                }
                onChange?.(productUserNeed);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};

export default UserNeedInfoCard;
